import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import { AccountCircle, Logout, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const UserProfileMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const getUserInitials = () => {
    if (!user) return '?';
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  };

  const getUserFullName = () => {
    if (!user) return 'User';
    return `${user.firstName} ${user.lastName}`;
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: Record<string, string> = {
      ADMIN: 'Administrator',
      USER: 'User',
    };
    return roleLabels[role] || role;
  };

  return (
    <>
      <Tooltip title="Account">
        <IconButton onClick={handleMenu} color="inherit">
          {user ? (
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.875rem' }}>
              {getUserInitials()}
            </Avatar>
          ) : (
            <AccountCircle />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 220, mt: 1 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {getUserFullName()}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {user?.email}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            {user && getRoleLabel(user.role)}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleProfile} sx={{ mt: 0.5, gap: 1.5 }}>
          <Person fontSize="small" />
          My Profile
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <Logout fontSize="small" />
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};
